// Utilitários do modelo "Panorama" (topo 13 · busca 16 · card 10 · rodapé 10) — linguagem visual
// de um site de referência (Mulish, barra de vidro em pílula, painéis em degradê azul com raio
// grande num canto só, acento quente) reproduzida em CSS modules NOSSOS, com as CORES DO TENANT.
// Só as 4 peças do Panorama importam daqui — não é componente compartilhado dos ~300 sites.
// Helpers genéricos (contatos, menu, redes, WhatsApp, URL/preço/foto do imóvel) são os MESMOS do
// Horizonte/Atelier, re-exportados para as peças importarem de um lugar só.
import type { CSSProperties } from 'react';
import { getContrastRatio, isColorLight, NumeroFormatado, toPrice } from '../../../utils/FunctionsGlobals';
import { hexParaRgba, misturarCores } from '../horizonte/horizonte';
import type { FormatadorPreco } from '../../../utils/moedaSite';

export { contatosDoTenant, hexParaRgba, linkWhatsApp, linkWhatsAppDoTenant, menuDoTenant, mesmaRota, misturarCores, redesDoTenant, useMovimentoReduzido } from '../horizonte/horizonte';
export type { ItemMenu, TelefoneTenant } from '../horizonte/horizonte';
export { finalidadeDoImovel, fotoOtimizada, fotosDoImovel, nomeDaEmpresa, stripHtml, tituloDoImovel, urlDaListagem, urlDoImovel } from '../atelier/atelier';

// Curva das entradas do modelo (desacelera longo no fim) — a mesma dos modelos 8/9.
export const EASE_PANORAMA = [0.22, 1, 0.36, 1] as const;

/** Paleta de RESERVA (tokens do site de referência): entra só onde o tenant não tem cor utilizável. */
export const RESERVA = {
    escuro: '#003265',
    vivo: '#2d67e8',
    profundo: '#001933',
    noite: '#001020',
    acento: '#ffb934',
};

export interface PaletaPanorama {
    /** marinho: fundo da barra rolada, dos painéis e do rodapé (texto branco lê ≥ 4.5:1). */
    escuro: string;
    /** escuro mais fundo (fim dos degradês). */
    profundo: string;
    /** quase preto (badge, texto sobre o acento claro). */
    noite: string;
    /** "azul vivo": início dos degradês, ícones de traço e botão "Detalhes". */
    vivo: string;
    /** acento quente (botões, títulos sobre foto/escuro). */
    acento: string;
    acentoHover: string;
    acentoAtivo: string;
    /** texto sobre o acento (botões) — calculado, nunca chutado. */
    acentoTexto: string;
    /** acento para TEXTO sobre fundo branco (card): escurecido até ler ≥ 3:1. */
    acentoEscuro: string;
    /** tons claros derivados do escuro (divisórias, fundos sutis). */
    claro: string;
    ceu: string;
}

const hexValido = (v: unknown): string => {
    let h = String(v ?? '')
        .trim()
        .toLowerCase();
    if (!h) return '';
    if (!h.startsWith('#')) h = `#${h}`;
    if (/^#[0-9a-f]{3}$/.test(h)) h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
    return /^#[0-9a-f]{6}$/.test(h) ? h : '';
};

/** Saturação HSL (0–1): cinza/branco/preto não servem de base para um modelo "azul". */
const saturacao = (hex: string): number => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    if (max === min) return 0;
    const l = (max + min) / 2;
    const d = max - min;
    return l > 0.5 ? d / (2 - max - min) : d / (max + min);
};
const temCor = (hex: string): boolean => Boolean(hex) && saturacao(hex) >= 0.18;
/** luminância relativa (0–1) via contraste com o preto. */
const luminancia = (hex: string): number => getContrastRatio(hex, '#000000') * 0.05 - 0.05;
/** preto/grafite (sem saturação, bem escuro) também faz o papel do marinho — só branco/cinza claro não serve. */
const neutraEscura = (hex: string): boolean => Boolean(hex) && saturacao(hex) < 0.18 && luminancia(hex) < 0.12;
const serveDeBase = (hex: string): boolean => temCor(hex) || neutraEscura(hex);
/** acento: saturado, lê ≥ 3:1 sobre o escuro E claro o bastante p/ ser TEXTO sobre foto com véu (≥ 5:1 sobre preto) —
 *  um marinho lia 3:1 sobre um marrom e virava título ilegível no hero (cs2619). */
const serveDeAcento = (hex: string, escuro: string): boolean => temCor(hex) && getContrastRatio(hex, escuro) >= 3 && getContrastRatio(hex, '#000000') >= 5;

/** hex → [h (0–360), s (0–1), l (0–1)]. */
const hexParaHsl = (hex: string): [number, number, number] => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min;
    const sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h *= 60;
    return [h, sat, l];
};
const hslParaHex = (h: number, sat: number, l: number): string => {
    const k = (n: number) => (n + h / 30) % 12;
    const a = sat * Math.min(l, 1 - l);
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    const c = (v: number) => Math.round(v * 255).toString(16).padStart(2, '0');
    return `#${c(f(0))}${c(f(8))}${c(f(4))}`;
};
/** "Azul vivo" da referência (#2d67e8) a partir da cor principal: MESMA matiz, saturada e luminosa. */
const corViva = (base: string): string => {
    const [h, sat] = hexParaHsl(base);
    return hslParaHex(h, Math.max(sat, 0.68), 0.52);
};

/** Puxa `cor` para `alvo` até `texto` ler ≥ `minimo` sobre ela (passos de 5%). */
const ajustarAte = (cor: string, alvo: string, texto: string, minimo: number): string => {
    for (let t = 0; t <= 1.0001; t += 0.05) {
        const c = t === 0 ? cor : misturarCores(cor, alvo, t);
        if (getContrastRatio(texto, c) >= minimo) return c;
    }
    return alvo;
};

/** Escurece `cor` até ler ≥ 3:1 sobre branco (títulos/preços do card em cima do card branco). */
const ajustarAteSobreBranco = (cor: string, alvo: string): string => {
    for (let t = 0; t <= 1.0001; t += 0.05) {
        const c = t === 0 ? cor : misturarCores(cor, alvo, t);
        if (getContrastRatio(c, '#ffffff') >= 3) return c;
    }
    return alvo;
};

/**
 * Mapeamento p/ os 300 sites: marinho/escuros = a cor MAIS ESCURA do tenant que serve de base (saturada
 * ou preto/grafite; cor1 em empate, nunca branco/cinza claro), escurecida até texto branco ler ≥ 4.5:1;
 * acento = a OUTRA cor quando é saturada, contrasta com o escuro (≥ 3:1) e é clara o bastante p/ texto
 * sobre foto; senão o acento de reserva. Sem cor no tenant → paleta de reserva.
 * Ex.: laranja+marinho → marinho/laranja (como a referência); grafite+dourado → grafite/dourado.
 */
export const paletaPanorama = (site: any): PaletaPanorama => {
    const cor1 = hexValido(site?.cor1);
    const cor2 = hexValido(site?.cor2);
    const bases = [cor1, cor2].filter(serveDeBase);
    // a cor2 só toma o lugar da principal quando é CLARAMENTE mais escura (a clara tenta o acento)
    const base = bases.length === 2 && luminancia(cor2) < luminancia(cor1) * 0.6 ? cor2 : bases[0] ?? '';
    const outra = base === cor1 ? cor2 : cor1;

    const escuro = base ? ajustarAte(base, RESERVA.noite, '#ffffff', 4.5) : RESERVA.escuro;
    const profundo = misturarCores(escuro, '#000814', 0.45);
    const noite = misturarCores(escuro, '#000000', 0.68);
    // "azul vivo" = a principal na versão viva (mesma matiz, saturada e luminosa); texto branco tem de
    // ler ≥ 3:1 nos botões "Detalhes" — se não ler, escurece até ler.
    // base neutra (preto/grafite) não tem matiz: o "vivo" vira um cinza médio, não um vermelho de matiz 0
    const vivoBruto = !base ? RESERVA.vivo : temCor(base) ? corViva(base) : misturarCores(base, '#ffffff', 0.38);
    const vivo = getContrastRatio('#ffffff', vivoBruto) >= 3 ? vivoBruto : ajustarAte(vivoBruto, escuro, '#ffffff', 3);

    const candidato = serveDeAcento(outra, escuro) ? outra : '';
    const acento = candidato || RESERVA.acento;
    const acentoHover = misturarCores(acento, '#ffffff', 0.28);
    const acentoAtivo = misturarCores(acento, '#000000', 0.12);
    const textoClaro = getContrastRatio('#ffffff', acento);
    const textoEscuro = getContrastRatio(profundo, acento);
    const acentoTexto = isColorLight(acento) ? (textoEscuro >= 4.5 || textoEscuro >= textoClaro ? profundo : '#ffffff') : textoClaro >= 4.5 || textoClaro >= textoEscuro ? '#ffffff' : profundo;
    const acentoEscuro = ajustarAteSobreBranco(acento, noite);

    return {
        escuro,
        profundo,
        noite,
        vivo,
        acento,
        acentoHover,
        acentoAtivo,
        acentoTexto,
        acentoEscuro,
        claro: misturarCores(escuro, '#ffffff', 0.84),
        ceu: misturarCores(vivo, '#ffffff', 0.55),
    };
};

/** Variáveis CSS (--pn-*) que os 4 módulos consomem; o Builder pode sobrescrevê-las por CSS custom. */
export const varsPanorama = (site: any): CSSProperties => {
    const p = paletaPanorama(site);
    return {
        '--pn-escuro': p.escuro,
        '--pn-profundo': p.profundo,
        '--pn-noite': p.noite,
        '--pn-vivo': p.vivo,
        '--pn-acento': p.acento,
        '--pn-acento-hover': p.acentoHover,
        '--pn-acento-ativo': p.acentoAtivo,
        '--pn-acento-texto': p.acentoTexto,
        '--pn-acento-escuro': p.acentoEscuro,
        '--pn-claro': p.claro,
        '--pn-ceu': p.ceu,
        '--pn-escuro-veu': hexParaRgba(p.escuro, 0.32, 'rgba(0, 50, 101, ALPHA)'),
        '--pn-vivo-suave': hexParaRgba(p.vivo, 0.12, 'rgba(45, 103, 232, ALPHA)'),
    } as CSSProperties;
};

// ---------------------------------------------------------------------------
// Imóvel: ficha em grade (quartos · suítes · banheiros · vagas · área) e preços ROTULADOS.
// ---------------------------------------------------------------------------

export type ItemFichaPanorama = { icone: 'quartos' | 'suites' | 'banheiros' | 'vagas' | 'area'; texto: string };

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/** Até 4 itens com valor > 0, na ordem da referência (quartos, suítes, banheiros, vagas; área fecha). */
export const fichaPanorama = (slide: any, maximo = 4): ItemFichaPanorama[] => {
    const n = (v: any) => Number(v) || 0;
    const itens: ItemFichaPanorama[] = [];
    if (n(slide?.dormitorios) > 0) itens.push({ icone: 'quartos', texto: plural(n(slide.dormitorios), 'quarto', 'quartos') });
    if (n(slide?.suites) > 0) itens.push({ icone: 'suites', texto: plural(n(slide.suites), 'suíte', 'suítes') });
    if (n(slide?.banheiros) > 0) itens.push({ icone: 'banheiros', texto: plural(n(slide.banheiros), 'banheiro', 'banheiros') });
    if (n(slide?.garagems) > 0) itens.push({ icone: 'vagas', texto: plural(n(slide.garagems), 'vaga', 'vagas') });
    const area = slide?.area_privativa > 0 ? slide.area_privativa : slide?.area_total > 0 ? slide.area_total : slide?.terreno > 0 ? slide.terreno : 0;
    if (area > 0) itens.push({ icone: 'area', texto: `${NumeroFormatado(Number(area))} ${slide?.area_privativa > 0 || slide?.area_total > 0 ? 'm²' : slide?.area_tipo || 'm²'}` });
    return itens.slice(0, maximo);
};

export type PrecoRotulado = { rotulo: string; valor: string; consulta?: boolean };

/** "Valor de venda" / "Valor de locação" com as MESMAS regras de exibição dos cards existentes. */
export const precosRotulados = (slide: any, site: any, fmt?: FormatadorPreco): PrecoRotulado[] => {
    // 21/10: `fmt` (usePrecoMoeda) converte para a moeda do idioma; sem ele, o texto de sempre.
    const moeda = (preco: number) => {
        const simbolo = slide?.moeda_imovel && slide?.moeda_imovel != site?.moeda_tipo ? slide?.moeda_imovel : site?.moeda_tipo || 'R$';
        const original = `${simbolo} ${toPrice(preco, 2)}`;
        return fmt ? fmt(preco, simbolo, original) : original;
    };
    const showVenda = Boolean(slide?.exibir_preco_venda && slide?.preco_venda > 0);
    const showLocacao = Boolean(slide?.aluguel > 0 && slide?.exibir_preco_locacao && slide?.preco_locacao > 0);
    const out: PrecoRotulado[] = [];
    if (showVenda) out.push({ rotulo: 'Valor de venda', valor: moeda(slide.preco_venda) });
    else if (slide?.venda == 1) out.push({ rotulo: 'Valor de venda', valor: 'Consulte', consulta: true });
    if (showLocacao) out.push({ rotulo: 'Valor de locação', valor: moeda(slide.preco_locacao) });
    else if (slide?.aluguel == 1 && !out.length) out.push({ rotulo: 'Valor de locação', valor: 'Consulte', consulta: true });
    return out.slice(0, 2);
};

/** "CÓD. 1234 - CASA 3 QUARTOS" (sobretítulo do hero), sem caixa alta aqui — o CSS aplica. */
export const sobretituloDoImovel = (slide: any): string => {
    const dorm = Number(slide?.dormitorios) || 0;
    const tipo = [slide?.tipo?.nome, dorm > 0 ? plural(dorm, 'quarto', 'quartos') : ''].filter(Boolean).join(' ');
    return [slide?.referencia ? `Cód. ${slide.referencia}` : '', tipo].filter(Boolean).join(' - ');
};

/** "Bairro, Cidade" (UF quando o cadastro trouxer). */
export const localDoImovel = (slide: any): string => [slide?.bairro?.nome, slide?.cidade?.nome, slide?.cidade?.uf || slide?.estado?.sigla].filter(Boolean).join(', ');

/**
 * O site está no modelo "Panorama"? A assinatura é o TOPO 13 (mesma regra do Horizonte/Atelier):
 * quem escolhe o topo 13 ganha a linguagem do modelo também nos blocos da home (equipe, empresa,
 * blog, canal) e nas internas (/imoveis, ficha) — sem número novo no catálogo. Zero diff com
 * modelo_topo ≠ 13.
 */
export const ehModeloPanorama = (site: any): boolean => Number(site?.modelo_topo) === 13;
